import StickyTableResponsive from '../../common/StickyTableResponsive';
import SortableTh from '../../common/SortableTh';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTableSort } from '../../../hooks/useTableSort';
import {
  AdminDeactivateModal,
  AdminDeleteModal,
  AdminPeriodFields,
  AdminValidityActions,
  AdminValidityBadge,
  defaultAdminPeriod,
} from './KkpAdminShared';
import AdminFormLabel from '../../admin/AdminFormLabel';
import SearchableSelect from '../../SearchableSelect';
import KkpPlanningMagInlineCell from '../KkpPlanningMagInlineCell';
import { ADMIN_FIELD_HELP } from '../../../utils/adminHelpContent';
import {
  buildProduitRefLabelMap,
  buildProduitRefOptions,
} from '../../../utils/adminProduitRefOptions';
import clientAnnuaireService from '../../../services/clientAnnuaireService';
import kkpService from '../../../services/kkpService';
import { notify } from '../../common/AppToast';
import { AppButton, AppInput, AppModal, AppPillSwitch, AppSpinner } from '../../ui';

const VALIDITY_REF_SORT = {
  valeur: { type: 'string', getValue: (row) => row.value },
  productRef: { type: 'string', getValue: (row) => row.product_ref },
  dispoParMois: { type: 'number', getValue: (row) => Number(row.dispo_par_mois) || 0 },
  periode: { type: 'string', getValue: (row) => row.validity_label },
  statut: { type: 'string', getValue: (row) => row.validity_status },
};

const DISPO_PAR_MOIS_MAX = 24;

const emptyMagForm = () => ({
  value: '',
  product_ref: '',
  dispo_par_mois: '0',
  valid_from_year: new Date().getFullYear(),
  valid_from_month: new Date().getMonth() + 1,
});

const KkpAdminMagSection = () => {
  const [data, setData] = useState({ mag: {}, mois: [] });
  const [catalogueProduits, setCatalogueProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [magTab, setMagTab] = useState('formats');
  const [magForm, setMagForm] = useState(emptyMagForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivatePeriod, setDeactivatePeriod] = useState(defaultAdminPeriod);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteContextKey, setDeleteContextKey] = useState(null);

  const moisOptions = data.mois ?? [];
  const magList = data.mag?.[magTab] ?? [];
  const { sortedRows: sortedMag, sort: sortMag, toggleSort: toggleMag, getSortIcon: getMagIcon } = useTableSort(magList, VALIDITY_REF_SORT);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [result, catalogue] = await Promise.all([
        kkpService.listPlanningAdmin(),
        clientAnnuaireService.listProduits(),
      ]);
      setData({ mag: result.mag ?? {}, mois: result.mois ?? [] });
      setCatalogueProduits(catalogue);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const produitRefOptions = useMemo(
    () => buildProduitRefOptions(
      catalogueProduits,
      [
        magForm.product_ref,
        ...(data.mag?.formats ?? []).map((item) => item.product_ref),
      ],
    ),
    [catalogueProduits, data.mag?.formats, magForm.product_ref],
  );
  const produitRefLabelByValue = useMemo(
    () => buildProduitRefLabelMap(produitRefOptions),
    [produitRefOptions],
  );

  const wrapSave = async (fn, successMessage) => {
    setSaving(true);
    try {
      await fn();
      if (successMessage) notify.success(successMessage);
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openCreateModal = () => {
    setMagForm(emptyMagForm());
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setMagForm(emptyMagForm());
  };

  const submitCreate = () => wrapSave(
    () => kkpService.createPlanningMag({
      type: magTab === 'formats' ? 'format' : 'contenu',
      value: magForm.value,
      product_ref: magTab === 'formats' ? (magForm.product_ref || null) : null,
      dispo_par_mois: magTab === 'formats'
        ? Math.min(DISPO_PAR_MOIS_MAX, Math.max(0, parseInt(magForm.dispo_par_mois, 10) || 0))
        : undefined,
      valid_from_year: magForm.valid_from_year,
      valid_from_month: magForm.valid_from_month,
    }).then(() => closeModal()),
    'Valeur ajoutée',
  );

  const clearInlineEdit = () => {
    setEditingCell(null);
    setEditDraft(null);
  };

  const startInlineEdit = (id, field) => {
    const row = magList.find((item) => item.id === id);
    if (!row) return;
    setEditingCell({ venteId: id, field });
    if (field === 'product_ref') {
      setEditDraft(String(row.product_ref || '').toUpperCase());
      return;
    }
    if (field === 'dispo_par_mois') {
      setEditDraft(String(row.dispo_par_mois ?? 0));
      return;
    }
    setEditDraft(row[field] ?? '');
  };

  const saveInlineEdit = async (draftOverride) => {
    if (!editingCell || inlineSaving) return;
    const { venteId, field } = editingCell;
    const row = magList.find((item) => item.id === venteId);
    if (!row || (field !== 'product_ref' && field !== 'dispo_par_mois' && field !== 'value')) {
      clearInlineEdit();
      return;
    }

    const draft = draftOverride !== undefined ? draftOverride : editDraft;

    if (field === 'value') {
      const nextValue = String(draft ?? '').trim();
      if (!nextValue) {
        notify.error('Le libellé est requis.');
        return;
      }
      if (nextValue === row.value) {
        clearInlineEdit();
        return;
      }

      setInlineSaving(true);
      try {
        const option = await kkpService.updatePlanningOption(venteId, { value: nextValue });
        setData((prev) => ({
          ...prev,
          mag: {
            ...prev.mag,
            [magTab]: (prev.mag?.[magTab] ?? []).map((item) => (
              item.id === venteId ? { ...item, ...(option ?? {}), value: option?.value ?? nextValue } : item
            )),
          },
        }));
        clearInlineEdit();
        notify.success('Valeur enregistrée');
      } catch (err) {
        notify.error(err.message);
      } finally {
        setInlineSaving(false);
      }
      return;
    }

    if (field === 'dispo_par_mois') {
      const nextCount = Number.parseInt(String(draft ?? ''), 10);
      if (!Number.isFinite(nextCount) || nextCount < 0 || nextCount > DISPO_PAR_MOIS_MAX) {
        notify.error(`Le nombre de dispos par mois doit être compris entre 0 et ${DISPO_PAR_MOIS_MAX}.`);
        return;
      }
      const prevCount = Number(row.dispo_par_mois) || 0;
      if (nextCount === prevCount) {
        clearInlineEdit();
        return;
      }

      setInlineSaving(true);
      try {
        const option = await kkpService.updatePlanningOption(venteId, { dispo_par_mois: nextCount });
        setData((prev) => ({
          ...prev,
          mag: {
            ...prev.mag,
            [magTab]: (prev.mag?.[magTab] ?? []).map((item) => (
              item.id === venteId ? { ...item, dispo_par_mois: option?.dispo_par_mois ?? nextCount } : item
            )),
          },
        }));
        clearInlineEdit();
        notify.success('Nombre de dispos enregistré');
      } catch (err) {
        notify.error(err.message);
      } finally {
        setInlineSaving(false);
      }
      return;
    }

    const nextRef = String(draft ?? '').trim().toUpperCase() || null;
    const prevRef = row.product_ref ? String(row.product_ref).toUpperCase() : null;
    if (nextRef === prevRef) {
      clearInlineEdit();
      return;
    }

    setInlineSaving(true);
    try {
      await kkpService.updatePlanningOption(venteId, { product_ref: nextRef });
      setData((prev) => ({
        ...prev,
        mag: {
          ...prev.mag,
          [magTab]: (prev.mag?.[magTab] ?? []).map((item) => (
            item.id === venteId ? { ...item, product_ref: nextRef } : item
          )),
        },
      }));
      clearInlineEdit();
      notify.success('Réf. produit enregistrée');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setInlineSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setSaving(true);
    try {
      await kkpService.deactivatePlanningOption(deactivateTarget.id, deactivatePeriod);
      setDeactivateTarget(null);
      notify.success('Option désactivée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReactivate = async (id) => {
    setSaving(true);
    try {
      await kkpService.reactivatePlanningOption(id);
      notify.success('Option réactivée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await kkpService.deletePlanningOption(deleteTarget.id);
      setDeleteTarget(null);
      setDeleteContextKey(null);
      notify.success('Élément supprimé');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AppSpinner />;

  return (
    <>
      <AppPillSwitch
        className="mb-3"
        ariaLabel="Onglets magazine"
        value={magTab}
        onChange={(value) => {
          setMagTab(value);
          clearInlineEdit();
          closeModal();
        }}
        options={[
          { value: 'formats', label: 'Formats', icon: 'mdi:newspaper-variant-outline' },
          { value: 'contenus', label: 'Contenus', icon: 'mdi:text-box-outline' },
        ]}
      />

      {magTab === 'formats' && (
        <p className="text-sm text-secondary-light mb-3">
          Dispos / mois : nombre de lignes vides créées automatiquement chaque mois dans l’onglet Dispos du planning.
          0 n’en crée aucune. Le complément apparaît à l’ouverture du mois.
        </p>
      )}

      <div className="card admin-data-card">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span>
            {magTab === 'formats' ? 'Formats magazine' : 'Contenus éditoriaux'}
            {' '}
            ({sortedMag.length})
          </span>
          <AppButton
            size="sm"
            variant="danger-600"
            icon="mdi:plus"
            disabled={saving || inlineSaving}
            onClick={openCreateModal}
          >
            Ajouter
          </AppButton>
        </div>
        <StickyTableResponsive>
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <SortableTh sortKey="valeur" label="Valeur" sort={sortMag} onSort={toggleMag} getSortIcon={getMagIcon} />
                {magTab === 'formats' && (
                  <SortableTh sortKey="productRef" label="Réf. produit" sort={sortMag} onSort={toggleMag} getSortIcon={getMagIcon} />
                )}
                {magTab === 'formats' && (
                  <SortableTh sortKey="dispoParMois" label="Dispos / mois" sort={sortMag} onSort={toggleMag} getSortIcon={getMagIcon} />
                )}
                <SortableTh sortKey="periode" label="Période" sort={sortMag} onSort={toggleMag} getSortIcon={getMagIcon} />
                <SortableTh sortKey="statut" label="Statut" sort={sortMag} onSort={toggleMag} getSortIcon={getMagIcon} />
                <SortableTh label="Actions" className="text-end" sortable={false} />
              </tr>
            </thead>
            <tbody>
              {sortedMag.map((item) => (
                <tr key={item.id} className={item.validity_status === 'expired' ? 'opacity-50' : ''}>
                  <KkpPlanningMagInlineCell
                    venteId={item.id}
                    field="value"
                    type="text"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    className="fw-medium"
                  >
                    {item.value}
                  </KkpPlanningMagInlineCell>
                  {magTab === 'formats' && (
                    <KkpPlanningMagInlineCell
                      venteId={item.id}
                      field="product_ref"
                      type="searchable-select"
                      options={produitRefOptions}
                      allowEmpty
                      emptyLabel="— Aucune —"
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className="text-sm"
                    >
                      {produitRefLabelByValue.get(String(item.product_ref || '').toUpperCase()) || item.product_ref || '—'}
                    </KkpPlanningMagInlineCell>
                  )}
                  {magTab === 'formats' && (
                    <KkpPlanningMagInlineCell
                      venteId={item.id}
                      field="dispo_par_mois"
                      type="number"
                      inputMin={0}
                      inputMax={DISPO_PAR_MOIS_MAX}
                      editingCell={editingCell}
                      editDraft={editDraft}
                      onEditDraftChange={setEditDraft}
                      onStartEdit={startInlineEdit}
                      onSave={saveInlineEdit}
                      saving={inlineSaving}
                      className="text-end"
                    >
                      {item.dispo_par_mois ?? 0}
                    </KkpPlanningMagInlineCell>
                  )}
                  <td className="text-sm">{item.validity_label}</td>
                  <td><AdminValidityBadge status={item.validity_status} /></td>
                  <td className="text-end">
                    <AdminValidityActions
                      item={item}
                      saving={saving || inlineSaving}
                      onDeactivate={(t) => { setDeactivateTarget(t); setDeactivatePeriod(defaultAdminPeriod()); }}
                      onReactivate={handleReactivate}
                      onDelete={(t) => {
                        setDeleteTarget(t);
                        setDeleteContextKey(magTab === 'formats' ? 'mag-format' : 'mag-contenu');
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </StickyTableResponsive>
      </div>

      <AppModal
        show={modalOpen}
        title={magTab === 'formats' ? 'Ajouter un format magazine' : 'Ajouter un contenu éditorial'}
        onClose={closeModal}
        size="lg"
        centered
        contentAs="form"
        contentProps={{
          onSubmit: (e) => {
            e.preventDefault();
            submitCreate();
          },
        }}
        footer={(
          <>
            <AppButton type="button" variant="outline-secondary" onClick={closeModal} disabled={saving}>
              Annuler
            </AppButton>
            <AppButton type="submit" variant="danger-600" loading={saving}>
              Ajouter
            </AppButton>
          </>
        )}
      >
        <p className="text-secondary-light small mb-3">
          {magTab === 'formats'
            ? 'Format publicitaire visible dans le chemin de fer'
            : 'Rubrique éditoriale (Monde Food, Terroir…)'}
        </p>
        <div className="row g-3">
          <div className="col-md-6">
            <AdminFormLabel
              help={magTab === 'formats' ? ADMIN_FIELD_HELP.magFormatLabel.help : ADMIN_FIELD_HELP.magContenuLabel.help}
              helpExample={magTab === 'formats' ? ADMIN_FIELD_HELP.magFormatLabel.helpExample : ADMIN_FIELD_HELP.magContenuLabel.helpExample}
              required
            >
              Libellé
            </AdminFormLabel>
            <AppInput required value={magForm.value} onChange={(e) => setMagForm((f) => ({ ...f, value: e.target.value }))} />
          </div>
          {magTab === 'formats' && (
            <div className="col-md-6">
              <AdminFormLabel help={ADMIN_FIELD_HELP.productRef.help} helpExample={ADMIN_FIELD_HELP.productRef.helpExample}>
                Réf. produit Dolibarr
              </AdminFormLabel>
              <SearchableSelect
                value={magForm.product_ref}
                onChange={(value) => setMagForm((f) => ({ ...f, product_ref: value }))}
                options={produitRefOptions}
                allowEmpty
                emptyOptionLabel="— Aucune —"
                placeholder="Produit en vente…"
                emptyLabel="Aucun produit catalogue"
                disabled={saving}
              />
            </div>
          )}
          {magTab === 'formats' && (
            <div className="col-md-6">
              <AdminFormLabel help={ADMIN_FIELD_HELP.magFormatDispo.help} helpExample={ADMIN_FIELD_HELP.magFormatDispo.helpExample}>
                Dispos / mois
              </AdminFormLabel>
              <AppInput
                type="number"
                min={0}
                max={DISPO_PAR_MOIS_MAX}
                value={magForm.dispo_par_mois}
                onChange={(e) => setMagForm((f) => ({ ...f, dispo_par_mois: e.target.value }))}
              />
            </div>
          )}
          <AdminPeriodFields
            year={magForm.valid_from_year}
            month={magForm.valid_from_month}
            moisOptions={moisOptions}
            onYearChange={(y) => setMagForm((f) => ({ ...f, valid_from_year: y }))}
            onMonthChange={(m) => setMagForm((f) => ({ ...f, valid_from_month: m }))}
          />
        </div>
      </AppModal>

      <AdminDeactivateModal
        target={deactivateTarget}
        period={deactivatePeriod}
        moisOptions={moisOptions}
        saving={saving}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
        onPeriodChange={setDeactivatePeriod}
      />
      <AdminDeleteModal
        target={deleteTarget}
        contextKey={deleteContextKey}
        saving={saving}
        onClose={() => { setDeleteTarget(null); setDeleteContextKey(null); }}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default KkpAdminMagSection;
